import { mergeClasses, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountAmountStyles } from '../../../hooks/useAccountAmountStyles';
import { useDrillDownStyles } from '../../../hooks/useDrillDownStyles';
import { formatNumberForDisplay, parseNumericValue } from '../../../utils/NumberUtils';
import { EnterAmountsDialogButton } from '../EnterAmountsDialogButton';
import { JournalEntryDialogButton } from '../JournalEntryDialogButton';
import { EditableNumberDiv } from './EditableNumberDiv';
import { LastModifiedByIcon } from './LastModifiedByIcon';
import { IAccountAmountAdjustmentEntryRowProps } from './types';

function stopClickPropagation(e: React.MouseEvent): void {
    e.stopPropagation();
}

function reformatNumericText(value: string, numberFormatter: (v: number) => string): string {
    return numberFormatter(parseNumericValue(value, true));
}

export const AccountAmountAdjustmentEntryRow: FC<IAccountAmountAdjustmentEntryRowProps> = (
    props: IAccountAmountAdjustmentEntryRowProps
) => {
    const styles = useAccountAmountStyles();
    const drillDownStyles = useDrillDownStyles();
    const { className, journalEntryButtonDisplayState, onJournalEntryClick, row } = { ...props };
    const { t } = useTranslation();

    const entityLabelId = `${row.uiKey}-entity-label`;
    const entityValueId = `${row.uiKey}-entity-value`;
    const caseLabelId = `${row.uiKey}-case-label`;
    const caseValueId = `${row.uiKey}-case-value`;
    const jurisdictionLabelId = `${row.uiKey}-jurisdiction-label`;
    const jurisdictionValueId = `${row.uiKey}-jurisdiction-value`;
    const yearLabelId = `${row.uiKey}-year-label`;
    const yearValueId = `${row.uiKey}-year-value`;
    return (
        <TreeItem itemType='leaf' key={row.uiKey} value={row.uiKey!} onClick={stopClickPropagation}>
            <TreeItemLayout
                main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain, props.containerClassName) }}
                className={mergeClasses('adjustmentEntryRow', styles.alignItemsStart, styles.adjustmentEntryRow, className)}
            >
                <div
                    role='gridcell'
                    aria-labelledby={`${entityLabelId} ${entityValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.entityColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={entityLabelId} className={styles.fontWeightSemibold}>
                            {t('entity')}:
                        </span>
                        <div id={entityValueId}>{row.entity}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    aria-labelledby={`${caseLabelId} ${caseValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.caseColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={caseLabelId} className={styles.fontWeightSemibold}>
                            {t('case')}:
                        </span>
                        <div id={caseValueId}>{row.case}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    aria-labelledby={`${jurisdictionLabelId} ${jurisdictionValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.jurisdictionColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={jurisdictionLabelId} className={styles.fontWeightSemibold}>
                            {t('jurisdiction')}:
                        </span>
                        <div id={jurisdictionValueId}>{row.jurisdiction}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    aria-labelledby={`${yearLabelId} ${yearValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.periodColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={yearLabelId} className={styles.fontWeightSemibold}>
                            {t('year')}:
                        </span>
                        <div id={yearValueId}>{row.year}</div>
                    </div>
                </div>
                <div role='gridcell' className={mergeClasses(styles.alignContentCenter, styles.lastModifiedByColumn, styles.fontSize)}>
                    <LastModifiedByIcon lastModifiedBy={row.lastModifiedBy!} />
                </div>
                <div
                    role='gridcell'
                    className={mergeClasses(
                        styles.alignContentCenter,
                        row.isComplexAdjustment ? styles.disabled : styles.editableBackground,
                        styles.journalEntryButtonHeader,
                        styles.fontSize,
                        props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                    )}
                >
                    {journalEntryButtonDisplayState !== 'invisible' && (
                        <JournalEntryDialogButton
                            className={drillDownStyles.iconOnlyButton}
                            disabled={journalEntryButtonDisplayState === 'disabled'}
                            onButtonClick={onJournalEntryClick!}
                        />
                    )}
                </div>
                <div
                    role='gridcell'
                    className={mergeClasses(
                        styles.alignContentCenter,
                        row.isComplexAdjustment ? styles.disabled : styles.editableBackground,
                        styles.enterAmountsButtonHeader,
                        styles.fontSize,
                        props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                    )}
                >
                    {row.isComplexAdjustment && (
                        <EnterAmountsDialogButton className={drillDownStyles.iconOnlyButton} onButtonClick={props.onEnterAmountsClick} />
                    )}
                </div>
                {row.isComplexAdjustment ? (
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            styles.reportAmountColumn,
                            styles.alignContentCenter,
                            styles.fontSize,
                            row.isComplexAdjustment ? styles.disabled : styles.editableBackground,
                            props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                        )}
                        data-automation-key='amount'
                    >
                        {reformatNumericText(row.amount!, formatNumberForDisplay)}
                    </div>
                ) : (
                    <EditableNumberDiv
                        role='gridcell'
                        className={mergeClasses(
                            styles.reportAmountColumn,
                            styles.alignContentCenter,
                            styles.fontSize,
                            row.isComplexAdjustment ? styles.disabled : styles.editableBackground,
                            props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                        )}
                        value={row.amount!}
                        onBlur={props.onBlur}
                        data-automation-key='amount'
                    ></EditableNumberDiv>
                )}
            </TreeItemLayout>
        </TreeItem>
    );
};
